import { getBilling } from '../api.js';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  getBitrixState,
  getChannels,
  getSessions,
  getSubscription,
} from '../api.js';

import '../commercial.css';


const LABELS = {
  telegram_personal:
    'Telegram — личный аккаунт',
  telegram_bot:
    'Telegram — бот',
  max_personal:
    'MAX — личный аккаунт',
  max_bot:
    'MAX — бот',
  whatsapp_personal:
    'WhatsApp — личный аккаунт',
  whatsapp_business:
    'WhatsApp Business',
};


const ICONS = {
  telegram_personal:
    '/brand/messengers/telegram-personal.svg',
  telegram_bot:
    '/brand/messengers/telegram-bot.svg',
  max_personal:
    '/brand/messengers/max-personal.svg',
  max_bot:
    '/brand/messengers/max-bot.svg',
  whatsapp_personal:
    '/brand/messengers/whatsapp-personal.svg',
};


function navigate(page) {
  window.dispatchEvent(
    new CustomEvent(
      'svyazka:navigate',
      {
        detail: page,
      },
    ),
  );
}


function rows(value) {
  if (Array.isArray(value)) {
    return value;
  }

  return Array.isArray(value?.items)
    ? value.items
    : [];
}


function normalize(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}


function money(value) {
  return new Intl.NumberFormat(
    'ru-RU',
    {
      maximumFractionDigits: 0,
    },
  ).format(
    Number(value || 0),
  );
}


function personalStatus(session) {
  if (!session) {
    return {
      tone: 'muted',
      label: 'Отключён',
    };
  }

  const worker = normalize(
    session.worker_status
  );

  const auth = normalize(
    session.auth_state
  );

  if (
    worker === 'connected'
    || worker === 'healthy'
  ) {
    return {
      tone: 'ok',
      label: 'Подключён',
    };
  }

  if (auth === 'authorized') {
    return {
      tone: 'warn',
      label: 'Авторизация сохранена',
    };
  }

  if (
    session.needs_reauth
    || auth === 'failed'
    || worker === 'error'
    || worker === 'banned'
  ) {
    return {
      tone: 'error',
      label: 'Нужно переподключить',
    };
  }

  return {
    tone: 'muted',
    label: 'Не подключён',
  };
}


function CommercialDashboard() {
  const [channels, setChannels] =
    useState([]);

  const [sessions, setSessions] =
    useState([]);

  const [bitrix, setBitrix] =
    useState(null);

  const [
    subscription,
    setSubscription,
  ] = useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');


  const load = useCallback(
    async () => {
      setLoading(true);
      setError('');

      try {
        const [
          channelResult,
          sessionResult,
          bitrixResult,
          subscriptionResult,
        ] = await Promise.all([
          getChannels(),
          getSessions(),
          getBitrixState(),
          getSubscription(),
        ]);

        setChannels(
          Array.isArray(channelResult)
            ? channelResult
            : [],
        );

        setSessions(
          rows(sessionResult),
        );

        setBitrix(
          bitrixResult || null
        );

        const billingOffer = await getBilling()
      .catch(() => null);

    setSubscription(
      subscriptionResult
        ? {
            ...subscriptionResult,
            price_per_channel_rub_month:
              subscriptionResult.status === 'active'
              && Number(subscriptionResult.price_per_channel_rub_month) === 0
              ? 0
              : (billingOffer?.price_per_channel_rub_month
                ?? subscriptionResult.price_per_channel_rub_month),
          }
        : null
    );
      } catch (exc) {
        setError(
          exc?.message
          || 'Не удалось обновить данные'
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );


  useEffect(
    () => {
      load();
    },
    [load],
  );


  const sessionsByChannel = useMemo(
    () => new Map(
      sessions
        .filter(
          (item) => item.channel_id
        )
        .map(
          (item) => [
            String(item.channel_id),
            item,
          ],
        ),
    ),
    [sessions],
  );


  const activeChannels = Number(
    subscription?.active_channels
    ?? channels.filter(
      (item) => item.is_active
    ).length
  );

  const channelLimit = Number(
    subscription?.channel_limit
    ?? 0
  );

  const remaining = Number(
    subscription?.remaining_channels
    ?? Math.max(
      channelLimit - activeChannels,
      0,
    )
  );

  const unitPrice = Number(
    subscription
      ?.price_per_channel_rub_month
    ?? 3000
  );

  const bitrixConnected = Boolean(
    bitrix?.connected
    && !bitrix?.needs_reauth
  );


  const lineCounts = new Map();

  for (const channel of channels) {
    const line = String(
      channel.bitrix_line_id || ''
    ).trim();

    if (!line) {
      continue;
    }

    lineCounts.set(
      line,
      (
        lineCounts.get(line)
        || 0
      ) + 1,
    );
  }


  const setupRequired = channels.some(
    (channel) => {
      const line = String(
        channel.bitrix_line_id || ''
      ).trim();

      return (
        !line
        || (
          lineCounts.get(line)
          || 0
        ) > 1
      );
    },
  );


  return (
    <div className="sv-home">
      <section className="sv-hero">
        <div>
          <div className="sv-eyebrow">
            СВЯЗКА
          </div>

          <h1>
            Мессенджеры в Битрикс24
          </h1>

          <p>
            Telegram, MAX и WhatsApp
            подключаются к Открытым линиям.
            Сотрудники продолжают работать
            внутри Битрикс24.
          </p>

          <div className="sv-actions">
            <button
              className="btn-primary"
              type="button"
              onClick={
                () => navigate(
                  'connections'
                )
              }
            >
              + Подключить канал
            </button>

            <button
              className="btn-secondary"
              type="button"
              onClick={
                () => navigate(
                  'bitrix'
                )
              }
            >
              Настроить Битрикс24
            </button>
          </div>
        </div>

        <div className="sv-brand-card">
          <img
            src="/brand/svyazka-icon-192.png"
            alt=""
          />

          <strong>Связка</strong>

          <span>
            Telegram · MAX · WhatsApp
          </span>
        </div>
      </section>

      {error && (
        <div className="sv-alert error">
          <div>
            <strong>
              Не удалось обновить состояние
            </strong>
            <span>{error}</span>
          </div>

          <button
            className="btn-secondary"
            type="button"
            onClick={load}
          >
            Повторить
          </button>
        </div>
      )}

      {setupRequired && !loading && (
        <div className="sv-alert warning">
          <div>
            <strong>
              Есть незавершённая настройка
            </strong>

            <span>
              Для каждого канала нужна
              собственная Открытая линия.
            </span>
          </div>

          <button
            className="btn-secondary"
            type="button"
            onClick={
              () => navigate(
                'bitrix'
              )
            }
          >
            Проверить
          </button>
        </div>
      )}

      <section className="sv-kpis">
        <article>
          <span>Битрикс24</span>

          <strong>
            {loading
              ? 'Проверяем…'
              : bitrixConnected
                ? 'Подключён'
                : 'Требует внимания'}
          </strong>

          <small>
            {bitrixConnected
              ? (
                bitrix.portal_domain
                || 'Marketplace'
              )
              : 'Проверьте приложение'}
          </small>
        </article>

        <article>
          <span>Каналы</span>
          <strong>
            {loading
              ? '—'
              : channels.length}
          </strong>
          <small>
            {activeChannels} активных
          </small>
        </article>

        <article>
          <span>Лимит</span>
          <strong>
            {loading
              ? '—'
              : channelLimit}
          </strong>
          <small>
            Доступно ещё: {remaining}
          </small>
        </article>

        <article>
          <span>Стоимость</span>
          <strong>
            {money(unitPrice)} ₽
          </strong>
          <small>
            за 1 канал в месяц
          </small>
        </article>
      </section>

      <section className="sv-section">
        <div className="sv-section-head">
          <div>
            <h2>Ваши каналы</h2>
            <p>
              Один аккаунт или бот —
              одна отдельная Открытая линия.
            </p>
          </div>

          <button
            className="btn-primary"
            type="button"
            onClick={
              () => navigate(
                'connections'
              )
            }
          >
            Подключить канал
          </button>
        </div>

        {loading ? (
          <div className="sv-empty">
            Загружаем каналы…
          </div>
        ) : channels.length === 0 ? (
          <div className="sv-empty">
            <img
              src="/brand/svyazka-icon-64.png"
              alt=""
            />

            <strong>
              Пока нет подключённых каналов
            </strong>

            <span>
              Добавьте первый аккаунт
              или бот.
            </span>

            <button
              className="btn-primary"
              type="button"
              onClick={
                () => navigate(
                  'connections'
                )
              }
            >
              Подключить первый канал
            </button>
          </div>
        ) : (
          <div className="sv-channel-grid">
            {channels.map(
              (channel) => {
                const type = normalize(
                  channel.channel_type
                );

                const session =
                  sessionsByChannel.get(
                    String(channel.id)
                  );

                const state =
                  type.endsWith(
                    '_personal'
                  )
                    ? personalStatus(
                        session
                      )
                    : channel.is_active
                      ? {
                          tone: 'ok',
                          label: 'Активен',
                        }
                      : {
                          tone: 'muted',
                          label: 'Отключён',
                        };

                const line = String(
                  channel.bitrix_line_id
                  || ''
                ).trim();

                const lineOk = Boolean(
                  line
                  && (
                    lineCounts.get(line)
                    || 0
                  ) === 1
                );

                return (
                  <article
                    className={`sv-channel-card${state.tone === 'muted' ? ' sv-channel-card--disconnected' : ''}`}
                    key={channel.id}
                  >
                    <div className="sv-channel-top">
                      <div className="sv-channel-icon">
                        {ICONS[type] ? (
                          <img
                            src={ICONS[type]}
                            alt=""
                          />
                        ) : (
                          <span>С</span>
                        )}
                      </div>

                      <div className="sv-channel-title">
                        <strong>
                          {channel.name
                            || channel
                              .sender_phone_number
                            || LABELS[type]
                            || 'Канал'}
                        </strong>

                        <span>
                          {LABELS[type]
                            || type}
                        </span>
                      </div>

                      <span
                        className={
                          (
                            'sv-status '
                            + state.tone
                          )
                        }
                      >
                        {state.label}
                      </span>
                    </div>

                    <div className="sv-channel-info">
                      <div>
                        <span>Битрикс24</span>

                        <strong>
                          {lineOk
                            ? 'Настроено'
                            : 'Нужно настроить'}
                        </strong>
                      </div>

                      <div>
                        <span>Тариф</span>

                        <strong>
                          {money(
                            unitPrice
                          )} ₽/мес.
                        </strong>
                      </div>
                    </div>

                    {state.tone === 'muted' && (
                      <p className="sv-channel-disconnected-note" role="status">
                        Канал отключён. Сообщения сейчас не поступают.
                        Настройки сохранены — подключение можно восстановить.
                      </p>
                    )}
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={
                        () => navigate(
                          'connections'
                        )
                      }
                    >
                      Управление каналом
                    </button>
                  </article>
                );
              },
            )}
          </div>
        )}
      </section>

      <section className="sv-section">
        <div className="sv-section-head">
          <div>
            <h2>
              Как работает Связка
            </h2>

            <p>
              Подключение настраивается
              один раз, дальше сотрудники
              работают в Битрикс24.
            </p>
          </div>
        </div>

        <div className="sv-flow">
          <article>
            <b>1</b>
            <strong>
              Подключите мессенджер
            </strong>
            <p>
              Личный аккаунт или бот.
            </p>
          </article>

          <article>
            <b>2</b>
            <strong>
              Выберите сотрудников
            </strong>
            <p>
              Связка подготовит
              отдельную Открытую линию.
            </p>
          </article>

          <article>
            <b>3</b>
            <strong>
              Отвечайте из Битрикс24
            </strong>
            <p>
              Диалоги и CRM остаются
              в штатном интерфейсе.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
}


export default function CommercialDashboardWithInvoices(props) {
  return <CommercialDashboard {...props} />;
}
